import { IRIS_TEAM_CODE } from '@/config'
import ApiService from '@/service'
import { User, UserInfo } from '@/types'
import { moduleActionContext } from '@/vuex'
import { defineActions, defineGetters, defineModule, defineMutations } from 'direct-vuex'
import jwtDecode from 'jwt-decode'

const REFRESH_INTERVAL = 1000 * 60

let refreshTokenTimer: number | undefined = undefined
let authRefreshTokenTimer: number | undefined = undefined

export interface AuthState {
  token?: string
  user?: User
  authExpiresAt?: Date
  userInfo?: UserInfo
}

const userAndExpiryDateFromJwt = (token: string) => {
  const { email, exp } = jwtDecode<{ email: string; exp: number }>(token)
  const username = email.split('@')[0]
  const user = { email, username }
  const authExpiresAt = new Date(exp * 1000)

  return { user, authExpiresAt }
}

export const authActionContext = (context: any) => moduleActionContext(context, authModule)

const authModule = defineModule({
  namespaced: true,

  state: {
    user: undefined,
    authExpiresAt: undefined,
    userInfo: undefined,
  } as AuthState,

  getters: defineGetters<AuthState>()({
    isAdminTeam(state: AuthState) {
      const teamCode = state.userInfo && state.userInfo.team_code
      return teamCode === IRIS_TEAM_CODE
    },
  }),

  mutations: defineMutations<AuthState>()({
    // This is for unit test usage, so one can setup test state without being mega-intrusive
    REPLACE(state: AuthState, payload: Partial<AuthState>) {
      const keys = Object.keys(payload) as (keyof AuthState)[]

      keys.forEach((key: keyof AuthState) => {
        state[key] = payload[key] as never
      })
    },

    SET_AUTH_DATA(
      state: AuthState,
      {
        user,
        authExpiresAt,
      }: {
        user: User | undefined
        authExpiresAt: Date | undefined
      }
    ) {
      state.user = user
      state.authExpiresAt = authExpiresAt
    },

    SET_USER_INFO(state: AuthState, userInfo: UserInfo) {
      state.userInfo = userInfo
    },
  }),

  actions: defineActions({
    // Auth flow we go through when there was a pre-existing auth token
    async UPDATE_AUTH_TOKEN(ctx, token: string): Promise<void> {
      const { commit, dispatch } = authActionContext(ctx)

      const { user, authExpiresAt } = userAndExpiryDateFromJwt(token)
      localStorage.setItem('token', token)
      await commit.SET_AUTH_DATA({ user, authExpiresAt })

      await dispatch.LOAD_USER_INFO()
    },

    // Auth flow we go through when we have an authorization code and need to request auth token (and refresh token)
    async REQUEST_AUTH_AND_REFRESH_TOKEN(ctx, authorizationCode: string): Promise<void> {
      const { dispatch, rootDispatch } = authActionContext(ctx)

      try {
        const { data } = await ApiService.getTokens(authorizationCode)
        const { id_token, refresh_token } = data
        localStorage.setItem('refresh_token', refresh_token)
        await dispatch.UPDATE_AUTH_TOKEN(id_token)

        authRefreshTokenTimer = window.setTimeout(() => dispatch.REFRESH_AUTH_TOKEN(), REFRESH_INTERVAL)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed requesting auth and refresh token', err)

        rootDispatch.SHOW_NOTIFICATION({
          message: 'Terribly sorry. We failed logging in. Details: ' + (err as Error).toString(),
          state: 'negative',
        })
        dispatch.LOGOUT()
      }
    },

    async REFRESH_AUTH_TOKEN(ctx): Promise<void> {
      const { dispatch } = authActionContext(ctx)
      const refreshToken = localStorage.getItem('refresh_token')
      if (!refreshToken) {
        throw new Error('No refresh token found.')
      }

      try {
        const {
          data: { id_token },
        } = await ApiService.refreshTokens(refreshToken)
        await dispatch.UPDATE_AUTH_TOKEN(id_token)

        refreshTokenTimer = window.setTimeout(() => dispatch.REFRESH_AUTH_TOKEN(), REFRESH_INTERVAL)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Unable to refresh auth token', err)
      }
    },

    async LOAD_USER_INFO(ctx): Promise<UserInfo> {
      const { commit, state } = authActionContext(ctx)

      const { data: userInfo } = await ApiService.getUserInfo(state.user!.email)

      commit.SET_USER_INFO(userInfo)

      return userInfo
    },

    LOGOUT(ctx): Promise<void> {
      return new Promise(resolve => {
        const { commit } = authActionContext(ctx)

        localStorage.removeItem('token')
        localStorage.removeItem('refresh_token')

        commit.SET_AUTH_DATA({
          user: undefined,
          authExpiresAt: undefined,
        })
        clearTimeout(refreshTokenTimer)
        clearTimeout(authRefreshTokenTimer)
        resolve()
      })
    },
  }),
})

export default authModule
